import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/**
 * Generic CSV/Excel bulk-upload dialog shared by every master-data level
 * (Taluka/PHC/Sub-Centre/Village/Gatpravartak) — unlike the users bulk
 * upload (BulkUploadDialog.js), which is tightly coupled to user fields and
 * an Excel-with-dropdowns template, master data's shape here is uniform
 * enough ({row, status, message} results) that one dialog covers all 5.
 *
 * Props:
 *  - title: dialog heading, e.g. "PHC बल्क अपलोड"
 *  - columns: [{ key, label, required }] — describes the CSV/Excel columns
 *    this level expects, used both for the on-screen hint and the
 *    client-generated CSV template download.
 *  - uploadFn: (formData) => Promise<axios response> — one of the
 *    locationAPI.bulkUpload* functions.
 */
function MasterDataBulkUploadDialog({ open, onClose, onSuccess, title, columns, uploadFn }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const isValidFile = (file) =>
    file && /\.(xlsx|xls|csv)$/i.test(file.name);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!isValidFile(file)) {
        setError('कृपया फक्त CSV किंवा Excel फाइल निवडा (.csv, .xlsx)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (isValidFile(file)) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDownloadTemplate = () => {
    const header = columns.map((c) => c.key).join(',');
    const example = columns.map((c) => c.example || '').join(',');
    const csv = `${header}\n${example}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await uploadFn(formData);
      setUploadResult(response.data);
      if (response.data.errors === 0 && onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'अपलोड करताना त्रुटी आली');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            आवश्यक कॉलम्स:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {columns.map((c) => (
              <Chip
                key={c.key}
                label={c.required ? `${c.key} *` : c.key}
                size="small"
                color={c.required ? 'primary' : 'default'}
                variant="outlined"
              />
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ mt: 1 }}
          >
            नमुना CSV डाउनलोड करा
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!uploadResult && (
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: selectedFile ? 'success.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: selectedFile ? 'success.lighter' : 'grey.50',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              onChange={handleFileSelect}
            />
            <UploadIcon sx={{ fontSize: 40, color: selectedFile ? 'success.main' : 'grey.400', mb: 1 }} />
            <Typography variant="body2">
              {selectedFile ? selectedFile.name : 'फाइल निवडा किंवा येथे ड्रॅग करा (.csv, .xlsx)'}
            </Typography>
          </Box>
        )}

        {uploading && <LinearProgress sx={{ mt: 2 }} />}

        {uploadResult && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`एकूण: ${uploadResult.total}`} size="small" />
              <Chip label={`नवीन: ${uploadResult.created}`} color="success" size="small" />
              <Chip label={`अद्ययावत: ${uploadResult.updated}`} color="info" size="small" />
              <Chip label={`त्रुटी: ${uploadResult.errors}`} color={uploadResult.errors ? 'error' : 'default'} size="small" />
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>पंक्ती</TableCell>
                    <TableCell>स्थिती</TableCell>
                    <TableCell>तपशील</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadResult.results.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status === 'created' ? 'नवीन' : r.status === 'updated' ? 'अद्ययावत' : 'त्रुटी'}
                          size="small"
                          color={r.status === 'error' ? 'error' : r.status === 'created' ? 'success' : 'info'}
                        />
                      </TableCell>
                      <TableCell>{r.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>बंद करा</Button>
        {!uploadResult && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            अपलोड करा
          </Button>
        )}
        {uploadResult && (
          <Button variant="contained" onClick={() => { setSelectedFile(null); setUploadResult(null); }}>
            आणखी अपलोड करा
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default MasterDataBulkUploadDialog;
